import {
  Active,
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import styled from '@emotion/styled';
import classNames from 'classnames';
import React, { useCallback, useState, useEffect, useMemo } from 'react';

import { UploadFile } from './FileUpload';
import { ImageUploadButton } from './ImageUploadButton';
import { ImageUploadPreview } from './ImageUploadPreview';
import { Stack } from '../stack';
import { border_radius_styles_with_size, semantic_colors, shapes } from '../../foundation';
import { IconThreeBars, IconCircleFill } from '../icons/generated';
import { buildCSSWithLength, CSSValueWithLength, getZIndex } from '../../styles';

export interface ImageFile extends Omit<UploadFile, 'value'> {
  key?: string;
  value?: File | null;
  src?: string;
  removable?: boolean;
  label?: string;
}

export interface ImageUploadProps {
  className?: string;
  objectFit?: 'contain' | 'cover' | 'fill';
  size?: 'medium' | 'small';
  /**
   * size가 'medium'일 경우 width, height를 변경할 수 있습니다.
   * 최소 width: 60, height: 60
   */
  width?: number;
  height?: number;
  /**
   * 허용되는 이미지 확장자를 지정합니다.
   *
   * @example
   * accept="*" // 모든 이미지 타입, 기본값
   * accept="png"
   * accept="png, jpg, gif"
   * accept=".png, .jpg, .gif"
   * accept="image/png, image/jpg"
   *
   * @default '*'
   */
  accept?: string;
  readOnly?: boolean;
  disabled?: boolean;
  loading?: boolean;
  backgroundDark?: boolean;
  /**
   * HTMLInputElement['multiple']
   */
  multiple?: boolean;
  /**
   * 최대 업로드 파일 갯수
   */
  maxCount?: number;
  /**
   * FileList내 삭제버튼 사용 유무
   * @default true
   */
  showRemoveButton?: boolean;
  /**
   * FileList를 노출하려면 지정된 UploadFile 타입으로 전달해야합니다.
   */
  fileList?: ImageFile[];
  /**
   * draggable이 true인 경우 ImageUploadPreview 컴포넌트를 드래그하여 재정렬할 수 있습니다.
   * @default false
   */
  draggable?: boolean;
  /** @default border_radius_styles_with_size.small */
  borderRadius?: CSSValueWithLength;
  /**
   * 드래그 중인 아이템의 z-index를 지정합니다.
   * @default getZIndex('draggable')
   */
  z_index?: number;

  onChange?: (file: File | File[]) => void;
  /**
   * 파일 업로드 버튼 클릭시 실행되는 이벤트
   */
  onClick?: React.MouseEventHandler<HTMLInputElement>;
  /**
   * FileList 클릭시 인덱스 정보와 함께 전달합니다. 드래그시에는 실행되지 않습니다.
   */
  onClickFile?: (image_url: string, index: number) => void;
  /**
   * 삭제 버튼 클릭시 인덱스 정보와 함께  전달합니다. 드래그시에는 실행되지 않습니다.
   */
  onClickFileRemove?: (file: ImageFile, index: number) => void;
  /**
   * 드래그 종료시 재정렬된 파일리스트를 전달합니다.
   */
  onDragEnd?: (fileList: ImageFile[]) => void;
}

const SortableImagePreview = React.memo(
  ({
    id,
    children,
    draggable,
    disabled,
    readOnly,
    z_index,
    isDraggingItem = false,
    loading,
    size,
    borderRadius,
  }: {
    id: string | number;
    children: React.ReactNode;
    draggable: boolean;
    readOnly: boolean;
    disabled: boolean;
    z_index: number;
    isDraggingItem: boolean;
    loading: boolean;
    size: ImageUploadProps['size'];
    borderRadius: CSSValueWithLength;
  }) => {
    const ableToDrag = draggable && !disabled && !readOnly && !loading;

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
      id,
      disabled: !ableToDrag,
    });

    return (
      <SortableImagePreviewContainer
        ref={setNodeRef}
        transform={CSS.Transform.toString(transform) || 'none'}
        transition={transition || 'none'}
        zIndex={isDragging ? `${z_index}` : 'auto'}
        boxShadow={isDraggingItem ? shapes.shadow.low : 'none'}
        borderRadius={borderRadius}
        {...attributes}
      >
        {children}
        {ableToDrag && (
          <DragHandleButton size={size} disabled={disabled} {...listeners}>
            <IconCircleFill size={size === 'medium' ? 24 : 16} color={semantic_colors.content.primary} />
            <DragHandleIcon size={size === 'medium' ? 16 : 10} color='white' />
          </DragHandleButton>
        )}
      </SortableImagePreviewContainer>
    );
  },
);

export const ImageUpload = ({
  className,
  size = 'medium',
  width,
  height,
  accept = '*',
  objectFit = 'contain',
  readOnly,
  disabled,
  loading,
  backgroundDark,
  multiple,
  maxCount,
  showRemoveButton,
  fileList,
  draggable = false,
  z_index = getZIndex('draggable'),
  borderRadius = border_radius_styles_with_size.small,
  onChange,
  onClick,
  onClickFile,
  onClickFileRemove,
  onDragEnd,
}: ImageUploadProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [activeDragItem, setActiveDragItem] = useState<Active | null>(null);
  const [localFileList, setLocalFileList] = useState(fileList || []);

  useEffect(() => {
    setLocalFileList(fileList || []);
  }, [fileList]);

  const sortableItems = useMemo(() => localFileList.map((item) => item.key || '').filter(Boolean), [localFileList]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleChange = useCallback(
    (file: File | File[]) => {
      onChange?.(Array.isArray(file) ? file : [file]);
    },
    [onChange],
  );

  const handleDragStart = useCallback((event: { active: Active }) => {
    setIsDragging(true);
    setActiveDragItem(event.active);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setIsDragging(false);

      if (!localFileList || !over || active.id === over.id) {
        return;
      }

      const sourceIndex = localFileList.findIndex((item) => String(item.key) === String(active.id));
      const targetIndex = localFileList.findIndex((item) => String(item.key) === String(over.id));

      if (sourceIndex === -1 || targetIndex === -1) {
        console.warn('ImageUpload: Invalid drag operation', {
          sourceId: active.id,
          targetId: over.id,
          sourceIndex,
          targetIndex,
        });
        return;
      }

      try {
        const reorderedList = arrayMove(localFileList, sourceIndex, targetIndex);
        setLocalFileList(reorderedList); // DnD 에 따른 현재 순서를 유지하기 위해 즉시 업데이트
        onDragEnd?.(reorderedList);
      } catch (error) {
        console.error('ImageUpload: Failed to reorder fileList', { error, sourceIndex, targetIndex });
      }
    },
    [localFileList, onDragEnd],
  );

  const handleClickFile = useCallback(
    (url: string, index: number) => {
      if (!isDragging) {
        onClickFile?.(url, index);
      }
    },
    [onClickFile, isDragging],
  );

  const handleClickFileRemove = useCallback(
    (file: ImageFile, index: number) => {
      if (!isDragging) {
        onClickFileRemove?.(file, index);
      }
    },
    [onClickFileRemove, isDragging],
  );

  const renderImagePreview = useCallback(
    (file: ImageFile, index: number) => (
      <ImageUploadPreview
        size={size}
        width={width}
        height={height}
        src={file.src}
        file={file.value ?? undefined}
        objectFit={objectFit}
        label={file.label}
        alt={file.label}
        backgroundDark={backgroundDark}
        loading={(file.status === 'uploading' && !disabled) || loading}
        readOnly={readOnly}
        disabled={disabled}
        removable={file.removable || showRemoveButton}
        error={file.status === 'error'}
        onClick={(url) => handleClickFile(url, index)}
        onClickRemove={() => handleClickFileRemove(file, index)}
        borderRadius={borderRadius}
      />
    ),
    [
      size,
      width,
      height,
      objectFit,
      backgroundDark,
      disabled,
      loading,
      readOnly,
      showRemoveButton,
      borderRadius,
      handleClickFile,
      handleClickFileRemove,
    ],
  );

  return (
    <ImageUploadContainer direction='row' className={classNames('pds-file-upload', className)}>
      {/* 업로드 버튼 */}
      {(multiple || !localFileList || localFileList.length === 0) && (
        <ImageUploadButton
          accept={accept}
          size={size}
          width={width}
          height={height}
          readOnly={readOnly}
          disabled={Boolean(disabled || (maxCount && localFileList && maxCount <= localFileList.length))}
          multiple={multiple}
          className={className}
          onFileUpload={handleChange}
          loading={loading}
          onClick={onClick}
          borderRadius={borderRadius}
        />
      )}
      {/* 파일 목록 렌더링 */}
      {localFileList && localFileList.length > 0 && (
        <>
          {draggable ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={sortableItems} strategy={rectSortingStrategy}>
                {localFileList.map((file, index) => {
                  const uniqueKey = file.key || file.value?.name || file.url || file.src || index;
                  return (
                    <SortableImagePreview
                      key={uniqueKey}
                      id={uniqueKey}
                      draggable={draggable}
                      readOnly={readOnly ?? false}
                      disabled={disabled ?? false}
                      loading={loading ?? false}
                      z_index={z_index}
                      isDraggingItem={isDragging && uniqueKey === activeDragItem?.id}
                      size={size}
                      borderRadius={borderRadius}
                    >
                      {renderImagePreview(file, index)}
                    </SortableImagePreview>
                  );
                })}
              </SortableContext>
            </DndContext>
          ) : (
            <>
              {localFileList.map((file, index) => {
                const uniqueKey = file.key || file.value?.name || file.url || file.src || index;
                return <div key={uniqueKey}>{renderImagePreview(file, index)}</div>;
              })}
            </>
          )}
        </>
      )}
    </ImageUploadContainer>
  );
};

const ImageUploadContainer = styled(Stack)`
  gap: 8px;
  flex-wrap: wrap;
`;

const SortableImagePreviewContainer = styled.div<{
  transform: string;
  transition: string;
  zIndex: string;
  boxShadow: string;
  borderRadius: CSSValueWithLength;
}>`
  position: relative;
  transform: ${({ transform }) => transform};
  transition: ${({ transition }) => transition};
  z-index: ${({ zIndex }) => zIndex};
  ${({ boxShadow }) => boxShadow};
  ${({ borderRadius }) => buildCSSWithLength('border-radius', borderRadius)}
`;

const DragHandleButton = styled.button<{ disabled?: boolean; size: ImageUploadProps['size'] }>`
  position: absolute;
  top: ${({ size }) => (size === 'medium' ? '4px' : '-3px')};
  left: ${({ size }) => (size === 'medium' ? '4px' : '-3px')};
  cursor: grab;
  padding: 0;
  border: none;
  background: none;
  line-height: 0;
  &:active {
    cursor: grabbing;
  }

  ${({ disabled }) => disabled && 'cursor: not-allowed;'}
`;

const DragHandleIcon = styled(IconThreeBars)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;
